import { InteractionStatus } from '@azure/msal-browser';
import { lazy, Suspense, useMemo, useState } from 'react';
import {
  BrainCircuit,
  Component as ComponentIcon,
  Columns,
  FileText,
  LogOut,
  RefreshCw,
  Users,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import CreateNewJob from '@/features/jobs/CreateNewJob.tsx';
import { AppProvider } from './AppContext.tsx';
import CandidateList from '../features/candidates/CandidateList';
import KanbanBoard from '../features/pipeline/KanbanBoard.tsx';
import AICVScanner from '../features/scanner/AICVScanner';
import CandidateProfile from '../features/candidates/CandidateProfile.tsx';
import JDManagement from '../features/jobs/JDManagement';

import Analytics from '../features/analytics/Analytics';
import Login from '../features/auth/Login';
import { loginRequest } from '../auth/msalConfig';
import type { JobDescription } from '../types.ts';
import AppSidebar, { type SidebarNavItem } from '../components/AppSidebar.tsx';
import AppHeader from '../components/AppHeader.tsx';
import AddCandidateModal, { type NewCandidateInput } from '../components/AddCandidateModal.tsx';
import { useNotifications } from '../hooks/useNotifications.ts';
import { useCandidateProfile } from '../hooks/useCandidateProfile.ts';
import { useJobOptions } from '../hooks/useJobOptions.ts';
import { useAuthSession } from '../hooks/useAuthSession.ts';
import { useEmbeddedTheme } from '../hooks/useEmbeddedTheme.ts';
import { resolveEmbeddedMode } from './embedMode.ts';

const isDevToolsEnabled = process.env.NODE_ENV === 'development';
const UIComponentsPage = isDevToolsEnabled
  ? lazy(() => import('../features/ui-components/UIComponents'))
  : null;

const navItems: SidebarNavItem[] = [
  // { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/" },
  {
    id: 'candidates',
    label: 'Candidates',
    icon: Users,
    path: '/candidates',
  },
  {
    id: 'kanban',
    label: 'Kanban Pipeline',
    icon: Columns,
    path: '/pipeline',
  },
  {
    id: 'jds',
    label: 'JD Management',
    icon: FileText,
    path: '/jobs',
  },
  {
    id: 'scanner',
    label: 'AI CV Scanning',
    icon: BrainCircuit,
    path: '/scanner',
  },
  // { id: "analytics", label: "Analytics", icon: BarChart3, path: "/analytics" },
  // { id: "settings", label: "Settings", icon: Settings, path: "/settings" },
];

function SettingsView() {
  return (
    <div className="p-12 max-w-2xl mx-auto space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-text-main mb-2">System Settings</h2>
        <p className="text-text-subtle">Manage your recruitment platform configuration and data.</p>
      </div>

      <div className="bg-card p-6 rounded-xl border border-border-subtle shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Data Management</h3>
        <p className="text-base text-text-subtle mb-6">
          Reload latest recruitment data from backend APIs.
        </p>
        <button
          onClick={() => {
            if (window.confirm('Are you sure you want to reset all data?')) {
              window.location.reload();
            }
          }}
          className="flex items-center gap-2 px-4 py-2 bg-error-muted text-error-muted-foreground border border-error/30 rounded-lg font-medium hover:bg-error-muted/70 transition-all"
        >
          <RefreshCw size={16} /> Reload Recruitment Data
        </button>
      </div>
    </div>
  );
}

function CandidateProfileRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId: profileId } = useParams<{ applicationId: string }>();
  const { data: selectedCandidate, isLoading, isFetching } = useCandidateProfile(profileId);

  if (isLoading || (isFetching && !selectedCandidate)) {
    return <div className="p-8 text-sm text-text-subtle">Loading candidate...</div>;
  }

  if (!selectedCandidate) {
    return (
      <div className="p-8">
        <h2 className="text-2xl font-bold text-text-main">Candidate not found</h2>
        <p className="text-text-subtle mt-2">
          The selected candidate does not exist or may have been removed.
        </p>
      </div>
    );
  }

  const handleBack = () => {
    const from = (location.state as { from?: string } | undefined)?.from;
    if (from != null) {
      navigate(-1);
      return;
    }
    navigate('/candidates');
  };

  return <CandidateProfile candidate={selectedCandidate} onBack={handleBack} />;
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isEmbeddedMode = useMemo(() => resolveEmbeddedMode(location.search), [location.search]);
  const { theme, toggleTheme } = useEmbeddedTheme(isEmbeddedMode);
  const {
    accounts,
    activeAccount,
    instance,
    isAuthenticated,
    accessToken,
    backendUser,
    isSessionReady,
    sessionError,
    logoutNow,
    retrySessionSync,
    isAuthBusy,
    inProgress,
  } = useAuthSession(location.pathname, { embeddedMode: isEmbeddedMode });
  const isProtectedDataEnabled = Boolean(accessToken) && isSessionReady;
  const jds: JobDescription[] = useJobOptions(isProtectedDataEnabled);

  const recruitmentPageTransitionKey =
    location.pathname === '/' ||
    location.pathname === '/oauth2-redirect' ||
    location.pathname.startsWith('/candidates') ||
    location.pathname === '/pipeline';

  /** One key for list + pipeline + profile so AnimatePresence does not unmount/remount between them (avoids brief opacity flash). */
  const pageTransitionKey = recruitmentPageTransitionKey ? 'candidates-flow' : location.pathname;

  const headerUserName = useMemo(() => {
    if (backendUser?.display_name) return backendUser.display_name;
    if (!activeAccount?.name) return 'Guest User';
    return activeAccount.name;
  }, [activeAccount, backendUser]);

  const headerUserRole = useMemo(() => {
    if (backendUser?.email) return backendUser.email;
    return activeAccount?.username || 'Microsoft Account';
  }, [activeAccount, backendUser]);

  const runtimeNavItems = useMemo<SidebarNavItem[]>(() => {
    if (!isDevToolsEnabled) return navItems;
    return [
      ...navItems,
      {
        id: 'ui-components',
        label: 'UI Components',
        icon: ComponentIcon,
        path: '/ui-components',
      },
    ];
  }, []);

  const {
    notifications,
    handleNotificationClick,
    handleMarkAllNotificationsRead,
    isMarkingAllAsRead,
  } = useNotifications({
    enabled: isProtectedDataEnabled,
    onNavigateToApplication: (applicationId) =>
      navigate(`/candidates/${applicationId}`, { state: { from: location.pathname } }),
  });

  if (!isEmbeddedMode && isAuthBusy) {
    return (
      <div className="min-h-screen grid place-items-center bg-bg-main p-6">
        <div className="rounded-xl border border-border-subtle bg-card px-6 py-4 text-base font-semibold text-text-main shadow-sm">
          Checking Microsoft sign-in status...
        </div>
      </div>
    );
  }

  if (isEmbeddedMode && !isSessionReady) {
    if (sessionError) {
      return (
        <div className="min-h-screen grid place-items-center bg-bg-main p-6">
          <div className="w-full max-w-md rounded-xl border border-border-subtle bg-card p-6 shadow-sm">
            <h1 className="text-xl font-semibold text-text-main">
              Embedded session needs attention
            </h1>
            <p className="mt-2 text-sm leading-6 text-text-subtle">{sessionError}</p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={retrySessionSync}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                <RefreshCw size={16} />
                Request token again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen grid place-items-center bg-bg-main p-6">
        <div className="rounded-xl border border-border-subtle bg-card px-6 py-4 text-base font-semibold text-text-main shadow-sm">
          Waiting for authentication from the parent app...
        </div>
      </div>
    );
  }

  if (!isEmbeddedMode && !isAuthenticated && accounts.length === 0) {
    return (
      <Login
        isBusy={inProgress !== InteractionStatus.None}
        onSignIn={() => instance.loginRedirect(loginRequest)}
      />
    );
  }

  if (!isEmbeddedMode && isAuthenticated && !isSessionReady) {
    if (sessionError) {
      return (
        <div className="min-h-screen grid place-items-center bg-bg-main p-6">
          <div className="w-full max-w-md rounded-xl border border-border-subtle bg-card p-6 shadow-sm">
            <h1 className="text-xl font-semibold text-text-main">Session needs attention</h1>
            <p className="mt-2 text-sm leading-6 text-text-subtle">{sessionError}</p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={retrySessionSync}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                <RefreshCw size={16} />
                Try again
              </button>
              <button
                type="button"
                onClick={logoutNow}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-border-subtle bg-card px-4 py-2 text-sm font-semibold text-text-main transition-colors hover:bg-bg-muted"
              >
                <LogOut size={16} />
                Sign in again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen grid place-items-center bg-bg-main p-6">
        <div className="rounded-xl border border-border-subtle bg-card px-6 py-4 text-base font-semibold text-text-main shadow-sm">
          Preparing your session...
        </div>
      </div>
    );
  }

  const handleSignOut = () => {
    logoutNow();
  };

  const handleCreateCandidate = (newCandidate: NewCandidateInput) => {
    const selectedJd = jds.find((jd) => jd.id === newCandidate.jdId);
    const role = selectedJd?.title || newCandidate.role;
    console.info('Create candidate requested', { ...newCandidate, role });
  };

  return (
    <div className="flex h-screen bg-transparent overflow-hidden font-sans">
      {!isEmbeddedMode ? (
        <AppSidebar
          navItems={runtimeNavItems}
          activePath={location.pathname}
          isMobileOpen={isSidebarOpen}
          onCloseMobile={() => setIsSidebarOpen(false)}
          onSignOut={handleSignOut}
          onNavigate={(path) => {
            navigate(path);
            setIsSidebarOpen(false);
          }}
        />
      ) : null}

      <main className="flex-1 flex flex-col relative overflow-hidden">
        {!isEmbeddedMode ? (
          <AppHeader
            onToggleSidebar={() => setIsSidebarOpen((prev) => !prev)}
            onUploadCV={() => navigate('/scanner')}
            userName={headerUserName}
            userRole={headerUserRole}
            notifications={notifications}
            onNotificationClick={handleNotificationClick}
            onMarkAllAsRead={handleMarkAllNotificationsRead}
            isMarkingAllAsRead={isMarkingAllAsRead}
            theme={theme}
            onToggleTheme={toggleTheme}
          />
        ) : null}

        <div className="flex-1 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={pageTransitionKey}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="h-full overflow-auto p-4 md:p-6 xl:p-8"
            >
              <Routes>
                {/* <Route path="/" element={<Dashboard />} /> */}
                {/* <Route
                  path="/dashboard"
                  element={<Navigate to="/" replace />}
                /> */}
                <Route
                  path="/"
                  element={
                    <CandidateList
                      onSelectCandidate={(applicationId) =>
                        navigate(`/candidates/${applicationId}`, {
                          state: { from: location.pathname },
                        })
                      }
                    />
                  }
                />
                <Route
                  path="/oauth2-redirect"
                  element={
                    <CandidateList
                      onSelectCandidate={(applicationId) =>
                        navigate(`/candidates/${applicationId}`, {
                          state: { from: location.pathname },
                        })
                      }
                    />
                  }
                />
                <Route
                  path="/candidates"
                  element={
                    <CandidateList
                      onSelectCandidate={(applicationId) =>
                        navigate(`/candidates/${applicationId}`, {
                          state: { from: location.pathname },
                        })
                      }
                    />
                  }
                />
                <Route path="/candidates/:applicationId" element={<CandidateProfileRoute />} />
                <Route
                  path="/pipeline"
                  element={
                    <KanbanBoard
                      onSelectCandidate={(applicationId) =>
                        navigate(`/candidates/${applicationId}`, {
                          state: { from: location.pathname },
                        })
                      }
                    />
                  }
                />
                <Route path="/jobs" element={<JDManagement />} />
                <Route path="/jobs/new" element={<CreateNewJob />} />
                <Route path="/jobs/:jdId/edit" element={<CreateNewJob />} />
                <Route path="/scanner" element={<AICVScanner />} />
                {isDevToolsEnabled && UIComponentsPage ? (
                  <Route
                    path="/ui-components"
                    element={
                      <Suspense
                        fallback={
                          <div className="rounded-xl border border-border-subtle bg-card p-6 text-sm text-text-subtle">
                            Loading UI Components...
                          </div>
                        }
                      >
                        <UIComponentsPage />
                      </Suspense>
                    }
                  />
                ) : null}
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/settings" element={<SettingsView />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      <AddCandidateModal
        isOpen={isAddModalOpen}
        jds={jds}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleCreateCandidate}
      />
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}
